import React, { useEffect, useState } from 'react';
import { Table, message, Input, Select, Row, Col, Button, Space, Card } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../Config/axiosConfig';
import getClassPublishedColumns from '../../Config/getClassPublishedColumns';
import UpdateClassModal from '../UpdateClassModal/UpdateClassModal';

const { Option } = Select;

function Published() {
  const [allClasses, setAllClasses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [filters, setFilters] = useState({
    instructor: null,
    search: '',
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const navigate = useNavigate();

  // The API only understands `pageNumber` (no pageSize/filter params), so we
  // walk every page it reports and merge the results into one list. Table
  // pagination and filtering then run entirely client-side over that list,
  // instead of trying to keep a server page in sync with the Table's own
  // pagination (which is what produced the "blank after page 2" bug).
  const fetchClasses = async () => {
    setLoading(true);
    try {
      const firstResponse = await axiosInstance.post('/Public/get-paged-classes', { pageNumber: 1 });
      const firstData = firstResponse?.data?.data;

      if (!firstData || !Array.isArray(firstData.classes)) {
        console.error('Invalid data structure:', firstData);
        message.error('No class data received.');
        setAllClasses([]);
        setClasses([]);
        setInstructors([]);
        return;
      }

      let combined = firstData.classes;
      const totalPages = firstData.pages || 1;

      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const remainingResponses = await Promise.all(
          remainingPages.map(pageNumber =>
            axiosInstance.post('/Public/get-paged-classes', { pageNumber })
          )
        );
        remainingResponses.forEach(res => {
          const pageClasses = res?.data?.data?.classes;
          if (Array.isArray(pageClasses)) {
            combined = combined.concat(pageClasses);
          }
        });
      }

      setAllClasses(combined);
      setClasses(combined);

      // Build the instructor filter list from every class, not just one page
      const uniqueInstructors = [];
      const instructorMap = new Map();

      combined.forEach(cls => {
        if (cls.teacher && !instructorMap.has(cls.teacher.id)) {
          instructorMap.set(cls.teacher.id, true);
          uniqueInstructors.push({
            id: cls.teacher.id,
            name: cls.teacher.name || `${cls.teacher.firstName} ${cls.teacher.lastName}` || 'Unknown'
          });
        }
      });

      setInstructors(uniqueInstructors);
    } catch (error) {
      console.error('Error fetching classes:', error);
      console.error('Error details:', error.response?.data || error.message);
      message.error('Error fetching classes');
      setAllClasses([]);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  // Apply filters across every loaded class
  const applyFilters = () => {
    let result = [...allClasses];

    if (filters.instructor) {
      result = result.filter(cls =>
        cls.teacher && cls.teacher.id === filters.instructor
      );
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(cls =>
        (cls.title && cls.title.toLowerCase().includes(searchLower)) ||
        (cls.description && cls.description.toLowerCase().includes(searchLower)) ||
        (cls.category && cls.category.name && cls.category.name.toLowerCase().includes(searchLower))
      );
    }

    setClasses(result);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      instructor: null,
      search: '',
    });
    setClasses(allClasses);
  };

  const handleRowClick = (record) => {
    navigate(`/preview-class/${record.id}`);
  };

  const handleEditClass = (record) => {
    setSelectedClass(record);
    setIsModalVisible(true);
  };

  const handleUpdateSuccess = () => {
    fetchClasses();
  };

  const columns = getClassPublishedColumns(
    navigate,
    () => fetchClasses(),
    1,
    handleEditClass
  );

  return (
    <div className="student-admin-container">
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Published Classes</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {allClasses.length} classes loaded
              </span>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchClasses()}
                loading={loading}
                size="small"
              >
                Refresh
              </Button>
            </div>
          </div>
        }
      >
        {/* Filters Section */}
        <div style={{ marginBottom: 20 }}>
          <Row gutter={[16, 16]} align="middle">
            {/* Instructor Filter */}
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by Instructor"
                style={{ width: '100%' }}
                value={filters.instructor}
                onChange={(value) => handleFilterChange('instructor', value)}
                allowClear
                disabled={instructors.length === 0}
              >
                {instructors.map(instructor => (
                  <Option key={instructor.id} value={instructor.id}>
                    {instructor.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Search Filter */}
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by title, description, or category..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                onPressEnter={() => applyFilters()}
              />
            </Col>

            {/* Filter Actions */}
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Button
                  onClick={applyFilters}
                  icon={<FilterOutlined />}
                  type="primary"
                >
                  Apply Filters
                </Button>
                <Button
                  onClick={handleResetFilters}
                  disabled={!filters.instructor && !filters.search}
                >
                  Clear Filters
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Filter Summary */}
          {(filters.instructor || filters.search) && (
            <div style={{ marginTop: 16, padding: 8, backgroundColor: '#f6ffed', borderRadius: 4 }}>
              <span style={{ marginRight: 8 }}>Active Filters:</span>
              {filters.instructor && (
                <span style={{ marginRight: 16 }}>
                  Instructor: <strong>{instructors.find(i => i.id === filters.instructor)?.name}</strong>
                </span>
              )}
              {filters.search && (
                <span>
                  Search: <strong>"{filters.search}"</strong>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            Showing {classes.length} of {allClasses.length} total classes
          </div>
        </div>

        {/* Main Table */}
        <Table
          columns={columns}
          dataSource={classes}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} classes`,
            showLessItems: false,
            hideOnSinglePage: false,
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          scroll={{ x: 'max-content' }}
          bordered
        />
      </Card>

      {/* Update Modal */}
      <UpdateClassModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        classInfo={selectedClass}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </div>
  );
}

export default Published;
